import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Save,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import {
  getBillingCycleEoePreview,
  getBillingCyclePayerResponse,
  getBillingCycleVisitsAndSupplies,
  getBillingCycleWorksheet,
  refreshBillingCycleWorksheet,
  replaceBillingCycleWorksheet,
  setBillingCycleStatusReadyToSend,
  updateBillingWorksheet,
} from '../../services/api';
import type {
  ActiveBillingCycleItem,
  BillableStatus,
  BillingCycleVisitsAndSuppliesResponse,
  EoePreviewResponse,
  PayerResponseReason,
} from '../../services/api/types.gen';
import type { ClaimWorksheetValidationError } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { scrollToField } from '../../utils/scrollToField';
import { IntakeValidationErrorList } from '../intake/IntakeValidationErrorList';
import { PayerReasonList } from './claimReason/PayerReasonList';
import {
  dollarAmountsEqual,
  isValidDollarAmount,
  parseDollarAmount,
  sumDollarAmounts,
} from '../../utils/currencyUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { CurrencyInput } from '../CurrencyInput';
import { useNavigateWithNewTab } from '../../hooks/useNavigateWithNewTab';
import { PhoneLink } from '../PhoneLink';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import { patientEpisodePath } from '../../utils/navigation';
import { VisitSupplyBillableSection } from './claimWorksheet/VisitSupplyBillableSection';
import { ConditionCodesField } from './worksheet/ConditionCodesField';
import { EditSection } from './worksheet/EditSection';
import { buildInsuranceIdFields } from './worksheet/insuranceIdFields';
import { OccurrenceCodesField } from './worksheet/OccurrenceCodesField';
import { PayerInsuredSection } from './worksheet/PayerInsuredSection';
import { SavedUb04Preview } from './worksheet/SavedUb04Preview';
import { ServiceLinesSection } from './worksheet/ServiceLinesSection';
import { ShowAllUb04Fields } from './worksheet/ShowAllUb04Fields';
import {
  SHOW_ALL_FIELDS_LOCATORS,
  buildErrorMap,
  errorKey,
  fieldError,
  hasErrorForLocators,
  worksheetErrorInputClass,
} from './worksheet/validationErrors';
import { WorksheetFieldError } from './worksheet/WorksheetFieldError';
import { WorksheetDateInput } from './worksheet/WorksheetDateInput';
import { ValueCodesField } from './worksheet/ValueCodesField';
import {
  MAX_UB04_OCCURRENCE_SPANS,
  MAX_UB04_REMARKS_LENGTH,
  MAX_UB04_SERVICE_LINES,
  MAX_WORKSHEET_OCCURRENCE_CODES,
  type WorksheetApiRead,
  type UB04PayerRow,
  type UB04ProviderRow,
  type UB04ServiceLine,
  type WorksheetEditorState,
} from './worksheet/types';
import {
  editorStateToFullUpdate,
  isTerminalWorksheetStatus,
  worksheetToEditorState,
} from './worksheet/worksheetUtils';

interface ClaimWorksheetEditorProps {
  billingCycle: ActiveBillingCycleItem;
  onClose: () => void;
  onApproved?: () => void | Promise<void>;
  readOnly?: boolean;
  /** A preserved claim-version worksheet to display instead of the cycle row. */
  initialWorksheet?: WorksheetApiRead;
}

const UB04_ADMISSION_TYPE_OPTIONS = [
  { value: '1', label: '1 — Emergency' },
  { value: '2', label: '2 — Urgent' },
  { value: '3', label: '3 — Elective' },
  { value: '4', label: '4 — Newborn' },
  { value: '5', label: '5 — Trauma center' },
  { value: '9', label: '9 — Information not available' },
];

const UB04_ADMISSION_SOURCE_OPTIONS = [
  { value: '1', label: '1 — Physician referral' },
  { value: '2', label: '2 — Clinical referral' },
  { value: '3', label: '3 — HMO referral' },
  { value: '4', label: '4 — Transfer from a hospital' },
  { value: '5', label: '5 — Transfer from a skilled nursing facility' },
  { value: '6', label: '6 — Transfer from another health care facility' },
  { value: '7', label: '7 — Emergency room' },
  { value: '8', label: '8 — Court or law enforcement' },
  { value: '9', label: '9 — Information not available' },
];

const UB04_PATIENT_STATUS_OPTIONS = [
  { value: '01', label: '01 — Discharged to home or self-care' },
  { value: '02', label: '02 — Transferred to a short-term hospital' },
  { value: '03', label: '03 — Transferred to a skilled nursing facility' },
  { value: '04', label: '04 — Transferred to intermediate care' },
  { value: '05', label: "05 — Transferred to a children's or cancer hospital" },
  { value: '06', label: '06 — Transferred to another home health agency' },
  { value: '07', label: '07 — Left against medical advice' },
  { value: '09', label: '09 — Admitted as an inpatient to this hospital' },
  { value: '20', label: '20 — Expired' },
  { value: '30', label: '30 — Still a patient' },
  { value: '43', label: '43 — Transferred to a federal hospital' },
  { value: '50', label: '50 — Transferred to hospice at home' },
  { value: '51', label: '51 — Transferred to hospice in a facility' },
  { value: '61', label: '61 — Transferred to a swing bed' },
  { value: '62', label: '62 — Transferred to inpatient rehabilitation' },
  { value: '63', label: '63 — Transferred to a long-term care hospital' },
  { value: '64', label: '64 — Transferred to a Medicaid nursing facility' },
  { value: '65', label: '65 — Transferred to a psychiatric hospital' },
  { value: '66', label: '66 — Transferred to a critical access hospital' },
  { value: '70', label: '70 — Transferred to another health care institution' },
];

function UB04CodeSelect({
  ariaLabel,
  options,
  value,
  onValueChange,
  error,
}: {
  ariaLabel: string;
  options: { value: string; label: string }[];
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  /** Save-time validation message for this field (EMR-857). */
  error?: string;
}) {
  // Ties the error text to the trigger for screen readers, matching the
  // aria-describedby wiring on the Input-based worksheet fields.
  const errorId = useId();
  return (
    <>
      <Select value={value ?? ''} onValueChange={onValueChange}>
        <SelectTrigger
          selectSize='sm'
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={error ? worksheetErrorInputClass : undefined}
        >
          <SelectValue placeholder='Select code' />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <WorksheetFieldError id={errorId} error={error} />
    </>
  );
}

const displayValue = (value?: string | null): string =>
  value && value.trim() !== '' ? value : '-';

const displayDate = (value?: string | null): string =>
  formatDateToMMDDYYYY(value) || '-';

const getMiddleInitial = (middleName?: string | null): string =>
  middleName?.trim()?.charAt(0)?.toUpperCase() || '-';

interface FieldDef {
  label: string;
  value: ReactNode;
  mono?: boolean;
  span2?: boolean;
}

const renderField = (field: FieldDef) => (
  <div key={field.label} className={field.span2 ? 'col-span-2' : undefined}>
    <div className='text-xs text-muted-foreground'>{field.label}</div>
    <div
      className={`text-sm font-medium text-foreground ${
        field.mono ? 'font-mono' : ''
      }`}
    >
      {field.value}
    </div>
  </div>
);

const renderFieldSection = (title: string, fields: FieldDef[]) => (
  <section
    key={title}
    className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70'
  >
    <h3 className='mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600'>
      <span className='h-1.5 w-1.5 rounded-full bg-teal-500' />
      {title}
    </h3>
    <div className='grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4'>
      {fields.map(renderField)}
    </div>
  </section>
);

function worksheetReason(worksheet: WorksheetApiRead | null): string | null {
  return worksheet?.worksheet_read_only_reason ?? null;
}

/** Returns payer rows with `field` set on row A, creating row A if it is absent. */
const primaryPayerRowsWith = (
  payerRows: UB04PayerRow[] | null | undefined,
  field: keyof Omit<UB04PayerRow, 'row'>,
  value: string
): UB04PayerRow[] => {
  const rows = [...(payerRows ?? [])];
  const index = rows.findIndex(payer => payer.row === 'A');
  const primaryPayer = {
    ...(index >= 0 ? rows[index] : { row: 'A' as const }),
    [field]: value,
  };
  if (index >= 0) {
    rows[index] = primaryPayer;
  } else {
    rows.unshift(primaryPayer);
  }
  return rows;
};

const serviceLineChargesTotal = (lines: UB04ServiceLine[] | null | undefined) =>
  sumDollarAmounts((lines ?? []).map(line => String(line.charges ?? '')));

const estimatedAmountDueOf = (state: WorksheetEditorState): string =>
  (
    state.ub04.payer_rows?.find(payer => payer.row === 'A')
      ?.estimated_amount_due ?? ''
  ).trim();

const withEstimatedAmountDue = (
  state: WorksheetEditorState,
  value: string
): WorksheetEditorState => ({
  ub04: {
    ...state.ub04,
    payer_rows: primaryPayerRowsWith(
      state.ub04.payer_rows,
      'estimated_amount_due',
      value
    ),
  },
});

type SaveAction = 'save' | 'saveAndApprove';

export default function ClaimWorksheetEditor({
  billingCycle,
  onClose,
  onApproved,
  readOnly = false,
  initialWorksheet,
}: ClaimWorksheetEditorProps) {
  const billingCycleId = billingCycle.billing_cycle_id;
  const historicalWorksheet = initialWorksheet !== undefined;
  const [worksheet, setWorksheet] = useState<WorksheetApiRead | null>(null);
  const [fullEdit, setFullEdit] = useState<WorksheetEditorState | null>(null);
  const [savedFullEdit, setSavedFullEdit] =
    useState<WorksheetEditorState | null>(null);
  const [preview, setPreview] = useState<EoePreviewResponse | null>(null);
  const [payerReasons, setPayerReasons] = useState<PayerResponseReason[]>([]);
  const [payerClaimNumber, setPayerClaimNumber] = useState<string | null>(null);
  const [payerReasonsError, setPayerReasonsError] = useState<string | null>(
    null
  );
  const showPayerReasons =
    !historicalWorksheet &&
    payerReasons.length > 0 &&
    !(
      readOnly &&
      ['PAID', 'CANCELLED'].includes(billingCycle.billing_cycle_status)
    );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [worksheetMissing, setWorksheetMissing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingSourceData, setIsSavingSourceData] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [chargesConfirm, setChargesConfirm] = useState<{
    action: SaveAction;
    total: string;
    currentEstimate: string;
  } | null>(null);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  // EMR-880: proposed FL 4 change after an FL 17 discharge edit (null = no
  // dialog). `from` is the worksheet's current raw value, `to` the 4-char
  // replacement offered in the confirmation dialog.
  const [tobChangeProposal, setTobChangeProposal] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [previewReloadKey, setPreviewReloadKey] = useState(0);
  const [pdfPreviewModeOpen, setPdfPreviewModeOpen] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  // Server-computed save-time findings (EMR-857). Authoritative and sticky
  // until the next response, like the NOA's: they describe what was last
  // saved, so a local edit does not clear them.
  const [validationErrors, setValidationErrors] = useState<
    ClaimWorksheetValidationError[]
  >([]);
  // Scrolled into view when a save response carries findings, so the biller
  // at the footer Save button sees that something needs attention.
  const validationSummaryRef = useRef<HTMLDivElement>(null);
  const [visitsData, setVisitsData] =
    useState<BillingCycleVisitsAndSuppliesResponse | null>(null);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [pendingBillingStatusChanges, setPendingBillingStatusChanges] =
    useState<Record<string, BillableStatus>>({});
  const [pendingBulkSupplyCost, setPendingBulkSupplyCost] = useState<
    string | null
  >(null);

  const navigateTo = useNavigateWithNewTab();
  const canViewOrders = useStore(state =>
    state.hasPermission(PermissionSlug.ORDER_MANAGER)
  );

  const navigateAndClose = (path: string) => (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey) onClose();
    navigateTo(path)(e);
  };

  const applyWorksheet = useCallback(
    (next: WorksheetApiRead) => {
      setWorksheetMissing(false);
      setWorksheet(next);
      const full = worksheetToEditorState(next);
      setFullEdit(full);
      setSavedFullEdit(full ? JSON.parse(JSON.stringify(full)) : null);
      const errors = next.validation_errors ?? [];
      setValidationErrors(errors);
      // Historical worksheets are already read-only, so show every preserved
      // field without offering a collapsed view that hides claim data.
      // Findings behind the "Show all fields" toggle would otherwise be
      // invisible and unreachable, so open the section when one lands there.
      if (
        historicalWorksheet ||
        hasErrorForLocators(errors, SHOW_ALL_FIELDS_LOCATORS)
      ) {
        setShowAllFields(true);
      }
    },
    [historicalWorksheet]
  );

  // Kept separate from load() so a refresh can re-read it too: the service
  // lines a refresh rebuilds come from this same visit set, and a panel left
  // on the pre-refresh set contradicts the worksheet beside it (EMR-1242).
  const loadVisitsAndSupplies = useCallback(async () => {
    const visitsResp = await getBillingCycleVisitsAndSupplies({
      path: { billing_cycle_id: billingCycleId },
    });
    // The visits/supplies endpoint refuses (422) on unbillable data — e.g. a
    // cycle with no patient insurance. Surface that instead of silently
    // rendering an empty section.
    if (visitsResp?.error) {
      console.error(
        'Failed to load billing cycle visits and supplies:',
        visitsResp.error
      );
      setVisitsError(
        getBusinessErrorMessage(visitsResp) ??
          'Unable to load visits and supplies. Please try again.'
      );
      setVisitsData(null);
    } else {
      setVisitsError(null);
      setVisitsData(visitsResp?.data ?? null);
    }
  }, [billingCycleId]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setWorksheetMissing(false);
    try {
      if (initialWorksheet !== undefined) {
        // Historical claim versions must not read the cycle worksheet: after a
        // later replacement opens, that row points at a different claim.
        applyWorksheet(initialWorksheet);
        setPreview(null);
        setPayerReasons([]);
        setPayerClaimNumber(null);
        setPayerReasonsError(null);
        setVisitsData(null);
        setVisitsError(null);
        setPendingBillingStatusChanges({});
        setPendingBulkSupplyCost(null);
        return;
      }

      const [resp, previewResp, payerResp] = await Promise.all([
        getBillingCycleWorksheet({
          path: { billing_cycle_id: billingCycleId },
        }),
        getBillingCycleEoePreview({
          path: { billing_cycle_id: billingCycleId },
        }),
        // The `billingCycle` prop carries only the summary and the count, so
        // the reasons themselves have to be fetched here (EMR-1241).
        getBillingCyclePayerResponse({
          path: { billing_cycle_id: billingCycleId },
        }).catch(() => null),
        loadVisitsAndSupplies(),
      ]);
      // A silent empty list here reads as "the payer said nothing", which is
      // the one thing this surface exists to rule out. A biller correcting a
      // returned bill must not be shown a blank where the reasons belong.
      if (!payerResp || payerResp.error) {
        // Status only. This response body carries the payer's free-text reasons,
        // so the error object must never reach the console — a session replay
        // would capture exactly what this screen exists to keep out of logs.
        console.error(
          'Failed to load billing cycle payer response. HTTP status:',
          getHttpStatus(payerResp) ?? 'none'
        );
        setPayerReasonsError(
          'Unable to load what the payer said. Please try again.'
        );
      } else {
        setPayerReasonsError(null);
      }
      if (resp.error || !resp.data) {
        throw resp.error ?? new Error('Empty worksheet response');
      }
      if (previewResp.error || !previewResp.data) {
        throw previewResp.error ?? new Error('Empty preview response');
      }
      applyWorksheet(resp.data as WorksheetApiRead);
      setPreview(previewResp.data);
      setPayerReasons(payerResp?.data?.reasons ?? []);
      setPayerClaimNumber(payerResp?.data?.payer_claim_control_number ?? null);
      setPendingBillingStatusChanges({});
      setPendingBulkSupplyCost(null);
    } catch (err) {
      console.error('Failed to load claim worksheet:', err);
      if (getHttpStatus(err) === 404 && !historicalWorksheet) {
        setWorksheetMissing(true);
        setError(
          'This billing cycle does not have a worksheet yet. Refresh worksheet data to create one.'
        );
      } else {
        setError('Unable to load the claim worksheet. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    applyWorksheet,
    billingCycleId,
    historicalWorksheet,
    initialWorksheet,
    loadVisitsAndSupplies,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(fullEdit) !== JSON.stringify(savedFullEdit),
    [fullEdit, savedFullEdit]
  );

  const errorMap = useMemo(
    () => buildErrorMap(validationErrors),
    [validationErrors]
  );

  // Errors and warnings render as separate panels: an error blocks Create
  // Claim ("Fix ... before sending"), while a warning is advisory and must
  // not be presented as blocking.
  const { validationErrorItems, validationWarningItems } = useMemo(() => {
    const toItems = (findings: ClaimWorksheetValidationError[]) =>
      findings.map((error, index) => {
        const key = errorKey(error.locator, error.row);
        const inAdvancedSection = hasErrorForLocators(
          [error],
          SHOW_ALL_FIELDS_LOCATORS
        );
        return {
          // The locator+row key repeats when one input carries two findings, so
          // the index keeps React keys unique.
          id: `${key}-${index}`,
          label: error.message,
          // A document-level finding has no input to scroll to, so it renders
          // as plain text rather than a link that silently does nothing.
          onClick:
            error.locator === 'document'
              ? undefined
              : () => {
                  if (inAdvancedSection && !showAllFields) {
                    // Expand first, then scroll — the input does not exist in
                    // the DOM until the section renders (same dead-end rule
                    // the intake workflow follows).
                    setShowAllFields(true);
                    scrollToField(key, { delayMs: 50 });
                    return;
                  }
                  scrollToField(key);
                },
        };
      });
    return {
      validationErrorItems: toItems(
        validationErrors.filter(error => error.severity !== 'warning')
      ),
      validationWarningItems: toItems(
        validationErrors.filter(error => error.severity === 'warning')
      ),
    };
  }, [showAllFields, validationErrors]);

  const primaryPayerError = (locator: string) =>
    fieldError(errorMap, locator, 'A');

  const updateFullUb04 = (patch: Partial<WorksheetEditorState['ub04']>) => {
    if (!fullEdit) return;
    setFullEdit({ ub04: { ...fullEdit.ub04, ...patch } });
  };

  // EMR-880: FL 17 leaving "30" (still a patient) signals a discharge. When
  // FL 4 currently carries an interim claim (322 first / 323 continuing),
  // offer — via confirmation dialog, never silently — the discharge form:
  // 322 → 0321 (single-claim admit through discharge), 323 → 0324 (interim
  // last). Keyed off the current FL 4 value, not insurance config, so 329/327
  // and other manually set values are never touched. No inverse rule: setting
  // FL 17 back to 30 does not revert FL 4.
  const handlePatientStatusChange = (patient_status: string) => {
    if (!fullEdit) return;
    updateFullUb04({ patient_status });
    if (patient_status === '30') return;
    const currentTob = (fullEdit.ub04.type_of_bill ?? '').trim();
    const normalized =
      currentTob.length === 4 && currentTob.startsWith('0')
        ? currentTob.slice(1)
        : currentTob;
    const target =
      normalized === '322' ? '0321' : normalized === '323' ? '0324' : null;
    if (target) {
      setTobChangeProposal({ from: currentTob, to: target });
    }
  };

  const getProvider = (slot: UB04ProviderRow['slot']): UB04ProviderRow =>
    fullEdit?.ub04.providers?.find(provider => provider.slot === slot) ?? {
      slot,
    };

  const getPrimaryPayer = (): UB04PayerRow =>
    fullEdit?.ub04.payer_rows?.find(payer => payer.row === 'A') ?? {
      row: 'A',
    };

  const updatePrimaryPayerField = (
    field: keyof Omit<UB04PayerRow, 'row'>,
    value: string
  ) => {
    if (!fullEdit) return;
    setFullEdit({
      ub04: {
        ...fullEdit.ub04,
        payer_rows: primaryPayerRowsWith(
          fullEdit.ub04.payer_rows,
          field,
          value
        ),
      },
    });
  };

  // FL 55 mirrors the FL 42-48 charges total until a biller edits it away from
  // that total. While it is still tracking (or blank), keep it in step with the
  // service lines; once it has been overridden, leave it alone and let the
  // save-time confirmation dialog ask what to do.
  const handleServiceLinesChange = (service_lines: UB04ServiceLine[]) => {
    if (!fullEdit) return;
    const currentEstimate = estimatedAmountDueOf(fullEdit);
    const tracking =
      currentEstimate === '' ||
      dollarAmountsEqual(
        currentEstimate,
        serviceLineChargesTotal(fullEdit.ub04.service_lines)
      );
    const next: WorksheetEditorState = {
      ub04: { ...fullEdit.ub04, service_lines },
    };
    // With no lines left there is no total to mirror, so FL 55 goes back to
    // blank rather than 0.00 — the same rule requestSave applies at save time,
    // and it keeps "not entered" distinct from a real zero on the claim.
    const trackedEstimate =
      service_lines.length === 0 ? '' : serviceLineChargesTotal(service_lines);
    setFullEdit(
      tracking && trackedEstimate !== currentEstimate
        ? withEstimatedAmountDue(next, trackedEstimate)
        : next
    );
  };

  const updateProviderField = (
    slot: UB04ProviderRow['slot'],
    field: keyof Omit<UB04ProviderRow, 'slot'>,
    value: string
  ) => {
    if (!fullEdit) return;
    const providers = [...(fullEdit.ub04.providers ?? [])];
    const index = providers.findIndex(provider => provider.slot === slot);
    const nextProvider = {
      ...(index >= 0 ? providers[index] : { slot }),
      [field]: value,
    };
    if (index >= 0) {
      providers[index] = nextProvider;
    } else {
      providers.push(nextProvider);
    }
    const slotOrder: UB04ProviderRow['slot'][] = [
      'attending',
      'operating',
      'other1',
      'other2',
    ];
    providers.sort(
      (a, b) => slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot)
    );
    setFullEdit({ ub04: { ...fullEdit.ub04, providers } });
  };

  const serverEditable = worksheet?.worksheet_editable;
  const effectiveReadOnly =
    historicalWorksheet ||
    readOnly ||
    serverEditable === false ||
    isTerminalWorksheetStatus(billingCycle.billing_cycle_status);
  const requiresRefresh =
    worksheet?.requires_refresh ?? !worksheet?.ub04_claim_data;
  const busy = isSaving || isRefreshing || isSavingSourceData;
  const canSaveFull = Boolean(
    fullEdit && !requiresRefresh && !effectiveReadOnly
  );
  const remarksLength = fullEdit?.ub04.remarks?.length ?? 0;
  const remarksTooLong = remarksLength > MAX_UB04_REMARKS_LENGTH;

  const refetchAfterConflict = useCallback(async () => {
    try {
      const resp = await getBillingCycleWorksheet({
        path: { billing_cycle_id: billingCycleId },
      });
      if (resp.data) applyWorksheet(resp.data as WorksheetApiRead);
    } catch (err) {
      console.error('Failed to refetch claim worksheet after conflict:', err);
    }
  }, [applyWorksheet, billingCycleId]);

  const validateFullEdit = useCallback((state: WorksheetEditorState) => {
    if ((state.ub04.service_lines ?? []).length > MAX_UB04_SERVICE_LINES) {
      setSaveError('FL 42-48 supports 22 service lines. Remove extra lines.');
      return false;
    }
    const tooLongDescription = (state.ub04.service_lines ?? []).some(
      line => (line.revenue_description_or_ide ?? '').length > 24
    );
    if (tooLongDescription) {
      setSaveError('FL 43 service-line description supports 24 characters.');
      return false;
    }
    if ((state.ub04.remarks ?? '').length > MAX_UB04_REMARKS_LENGTH) {
      setSaveError(
        `FL 80 remarks supports ${MAX_UB04_REMARKS_LENGTH} characters. Shorten remarks.`
      );
      return false;
    }
    const spanCount = (state.ub04.occurrence_uses ?? []).filter(
      use => use.kind === 'span'
    ).length;
    if (spanCount > MAX_UB04_OCCURRENCE_SPANS) {
      setSaveError(
        `FL 35-36 supports ${MAX_UB04_OCCURRENCE_SPANS} occurrence spans. Remove extra spans.`
      );
      return false;
    }
    const missingOccurrenceDate = (state.ub04.occurrence_uses ?? []).find(
      use => use.kind === 'occurrence' && use.code.trim() !== '' && !use.date
    );
    if (missingOccurrenceDate) {
      setSaveError(
        `Occurrence code ${missingOccurrenceDate.code.trim()} is missing its date. Add the date or clear the code.`
      );
      return false;
    }
    const badChargeIndex = (state.ub04.service_lines ?? []).findIndex(
      line => !isValidDollarAmount(String(line.charges ?? ''))
    );
    if (badChargeIndex >= 0) {
      setSaveError(
        `Enter a valid dollar amount for FL 42-48 line ${
          badChargeIndex + 1
        } charges`
      );
      return false;
    }
    const primaryPayer = state.ub04.payer_rows?.find(
      payer => payer.row === 'A'
    );
    if (
      primaryPayer?.prior_payment &&
      !isValidDollarAmount(primaryPayer.prior_payment)
    ) {
      setSaveError('Enter a valid dollar amount for FL 54 Prior payment');
      return false;
    }
    if (
      primaryPayer?.estimated_amount_due &&
      !isValidDollarAmount(primaryPayer.estimated_amount_due)
    ) {
      setSaveError(
        'Enter a valid dollar amount for FL 55 Estimated amount due'
      );
      return false;
    }
    return true;
  }, []);

  const persistFull = useCallback(
    async (state: WorksheetEditorState): Promise<boolean> => {
      if (!validateFullEdit(state)) return false;
      setIsSaving(true);
      setSaveError(null);
      try {
        const resp = await replaceBillingCycleWorksheet({
          path: { billing_cycle_id: billingCycleId },
          body: editorStateToFullUpdate(state),
          throwOnError: true,
        });
        if (!resp.data) {
          throw new Error('Empty save response');
        }
        applyWorksheet(resp.data as WorksheetApiRead);
        setPreviewReloadKey(key => key + 1);
        if ((resp.data as WorksheetApiRead).validation_errors?.length) {
          // The findings panel sits at the top of a long dialog; a biller who
          // clicked Save from the footer would otherwise see nothing change
          // and reasonably conclude the values were accepted as-is.
          requestAnimationFrame(() =>
            validationSummaryRef.current?.scrollIntoView?.({
              behavior: 'smooth',
              block: 'nearest',
            })
          );
        }
        return true;
      } catch (err) {
        console.error('Failed to save claim worksheet:', err);
        if (getHttpStatus(err) === 409) {
          await refetchAfterConflict();
        }
        // Surface the server's business message (e.g. an invalid FL 52/53
        // certification value naming the payer row) instead of generic copy;
        // getBusinessErrorMessage only returns 4xx business messages.
        setSaveError(
          getBusinessErrorMessage(err) ??
            'Failed to save worksheet. Please try again.'
        );
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [applyWorksheet, billingCycleId, refetchAfterConflict, validateFullEdit]
  );

  const saveSourceData = useCallback(async () => {
    const visitUpdates = Object.entries(pendingBillingStatusChanges).map(
      ([visit_id, new_billing_status]) => ({ visit_id, new_billing_status })
    );
    if (visitUpdates.length === 0 && pendingBulkSupplyCost === null)
      return true;
    setIsSavingSourceData(true);
    setSaveError(null);
    try {
      const resp = await updateBillingWorksheet({
        body: {
          billing_cycle_id: billingCycleId,
          visit_updates: visitUpdates,
          bulk_supply_cost:
            pendingBulkSupplyCost !== null
              ? parseDollarAmount(pendingBulkSupplyCost)
              : undefined,
        },
      });
      if (resp.error) throw resp.error;
      setPendingBillingStatusChanges({});
      setPendingBulkSupplyCost(null);
      setSaveError(
        'Source data saved. Refresh worksheet data to rebuild service lines.'
      );
      return true;
    } catch (err) {
      console.error('Failed to save worksheet source data:', err);
      setSaveError('Failed to save source data. Please try again.');
      return false;
    } finally {
      setIsSavingSourceData(false);
    }
  }, [billingCycleId, pendingBillingStatusChanges, pendingBulkSupplyCost]);

  const runSaveAction = useCallback(
    async (action: SaveAction, state: WorksheetEditorState) => {
      if (!(await persistFull(state))) return;
      if (action === 'save') return;
      setIsSaving(true);
      setSaveError(null);
      try {
        const resp = await setBillingCycleStatusReadyToSend({
          path: { billing_cycle_id: billingCycleId },
        });
        if (resp.error) throw resp.error;
        await onApproved?.();
        onClose();
      } catch (err) {
        console.error('Failed to approve claim:', err);
        setSaveError('Saved, but failed to approve. Please try Approve again.');
      } finally {
        setIsSaving(false);
      }
    },
    [billingCycleId, onApproved, onClose, persistFull]
  );

  // FL 55 is a biller-controlled override, so a mismatch with the FL 42-48
  // charges total is a question rather than an error: fill it in silently when
  // it is blank, save straight through when it already agrees, and otherwise
  // ask before touching it.
  const requestSave = useCallback(
    (action: SaveAction) => {
      if (!fullEdit) return;
      // Clear the last attempt's message here rather than leaving it to
      // persistFull: this attempt can stop at the FL 55 question, which would
      // otherwise leave a stale failure on screen behind the dialog and still
      // showing after Cancel, as though this save had failed too.
      setSaveError(null);
      // Validate up front so a bad field is reported instead of hidden behind
      // the FL 55 question; persistFull re-checks the state it actually sends.
      if (!validateFullEdit(fullEdit)) return;
      const lines = fullEdit.ub04.service_lines ?? [];
      const total = serviceLineChargesTotal(lines);
      const currentEstimate = estimatedAmountDueOf(fullEdit);
      if (currentEstimate === '') {
        // With no service lines there is no total worth writing into FL 55.
        if (lines.length === 0) {
          void runSaveAction(action, fullEdit);
          return;
        }
        void runSaveAction(action, withEstimatedAmountDue(fullEdit, total));
        return;
      }
      if (dollarAmountsEqual(currentEstimate, total)) {
        void runSaveAction(action, fullEdit);
        return;
      }
      setChargesConfirm({ action, total, currentEstimate });
    },
    [fullEdit, runSaveAction, validateFullEdit]
  );

  // The resolved FL 55 is handed to the save rather than written into editor
  // state first: a successful persistFull re-seeds fullEdit from the saved
  // worksheet anyway, so setting it up front would only serve to destroy the
  // biller's override when the save fails — and let the retry go through
  // silently, without asking again.
  const confirmSaveWithTotal = () => {
    if (!chargesConfirm || !fullEdit) return;
    setChargesConfirm(null);
    void runSaveAction(
      chargesConfirm.action,
      withEstimatedAmountDue(fullEdit, chargesConfirm.total)
    );
  };

  const confirmSaveKeepingEstimate = () => {
    if (!chargesConfirm || !fullEdit) return;
    const action = chargesConfirm.action;
    setChargesConfirm(null);
    void runSaveAction(action, fullEdit);
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setSaveError(null);
    try {
      const resp = await refreshBillingCycleWorksheet({
        path: { billing_cycle_id: billingCycleId },
      });
      if (resp.error || !resp.data) {
        // Rethrow in the shape the error helpers expect: keeping the status
        // alongside the body is what lets getBusinessErrorMessage gate 5xx
        // bodies out while still surfacing a 4xx business message.
        throw resp.error
          ? { response: { status: resp.response?.status, data: resp.error } }
          : new Error('Empty refresh response');
      }
      applyWorksheet(resp.data as WorksheetApiRead);
      setPreviewReloadKey(key => key + 1);
      // The refresh rebuilt the service lines from the current visit set, so
      // re-read that set too. Otherwise the Source Data > Visits panel keeps
      // showing the pre-refresh visits and contradicts the worksheet.
      await loadVisitsAndSupplies();
      // The refresh can also pick up a changed patient address, so re-read the
      // chart preview instead of leaving the pre-refresh demographics on screen.
      let previewReloadFailed = false;
      try {
        const previewResp = await getBillingCycleEoePreview({
          path: { billing_cycle_id: billingCycleId },
        });
        if (previewResp.error || !previewResp.data) {
          throw previewResp.error ?? new Error('Empty preview response');
        }
        setPreview(previewResp.data);
      } catch (err) {
        previewReloadFailed = true;
        console.error('Failed to reload claim worksheet chart details:', err);
        setSaveError(
          'Worksheet refreshed, but chart details could not be reloaded. Close and reopen the worksheet.'
        );
      }
      // A successful refresh can still find the cycle ungroupable; the backend
      // then clears the HIPPS code and says why, in copy written for a biller.
      // Show it — otherwise the code just vanishes from the sheet unexplained.
      // A stale-chart failure above outranks it: that one says the sheet on
      // screen is not what the chart holds.
      if (!previewReloadFailed && resp.data.hipps_unavailable_reason) {
        setSaveError(resp.data.hipps_unavailable_reason);
      }
    } catch (err) {
      console.error('Failed to refresh claim worksheet:', err);
      if (getHttpStatus(err) === 409) {
        await refetchAfterConflict();
      }
      // Surface the server's business message (e.g. visits missing a charted
      // time in/out, which names the dates to fix) instead of generic copy;
      // getBusinessErrorMessage only returns 4xx business messages, so 5xx
      // bodies still fall back below.
      setSaveError(
        getBusinessErrorMessage(err) ??
          'Failed to refresh worksheet data. Please try again.'
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [
    applyWorksheet,
    billingCycleId,
    loadVisitsAndSupplies,
    refetchAfterConflict,
  ]);

  const demographics = preview?.patient_demographics;
  const address = preview?.patient_address;
  const physician = preview?.physician_information;
  const episodeInfo = preview?.episode_information;
  const admissionInfo = preview?.admission_information;

  const patientFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(demographics?.last_name) },
    { label: 'First name', value: displayValue(demographics?.first_name) },
    {
      label: 'Middle initial',
      value: getMiddleInitial(demographics?.middle_name),
    },
    { label: 'Gender', value: displayValue(demographics?.gender) },
    { label: 'Date of birth', value: displayDate(demographics?.date_of_birth) },
    ...buildInsuranceIdFields(demographics),
    {
      label: 'Medical record number',
      value: displayValue(demographics?.mrn),
      mono: true,
    },
    {
      label: 'Phone number',
      value: <PhoneLink phone={demographics?.phone} fallback='-' />,
    },
    { label: 'Address', value: displayValue(address?.address), span2: true },
    { label: 'City', value: displayValue(address?.city) },
    { label: 'State', value: displayValue(address?.state) },
    { label: 'Zip', value: displayValue(address?.zip_code) },
  ];

  const physicianFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(physician?.last_name) },
    { label: 'First name', value: displayValue(physician?.first_name) },
    { label: 'Physician NPI', value: displayValue(physician?.npi), mono: true },
    { label: 'PECOS status', value: displayValue(physician?.pecos_status) },
  ];

  const episodeAdmissionFields: FieldDef[] = [
    { label: 'Type of bill', value: displayValue(episodeInfo?.type_of_bill) },
    { label: 'Start of care date', value: displayDate(episodeInfo?.soc_date) },
    {
      label: 'Assessment date',
      value: displayDate(episodeInfo?.assessment_date),
    },
    {
      label: 'Episode start date',
      value: displayDate(episodeInfo?.episode_start_date),
    },
    {
      label: 'First billable visit',
      value: displayDate(episodeInfo?.first_billable_visit),
    },
    {
      label: 'Point of origin',
      value: displayValue(admissionInfo?.point_of_origin),
    },
    {
      label: 'Admission type',
      value: displayValue(admissionInfo?.admission_type),
    },
  ];

  const chartPath =
    billingCycle.patient_id && billingCycle.episode_id
      ? patientEpisodePath(billingCycle.patient_id, billingCycle.episode_id)
      : null;
  const patientName = billingCycle.patient_name || '-';
  const headerRest = [
    `MRN ${billingCycle.patient_mrn || '-'}`,
    billingCycle.insurance_name || '-',
    `${displayDate(billingCycle.billing_period_start)} – ${displayDate(
      billingCycle.billing_period_end
    )}`,
  ].join(' · ');

  return (
    <>
      <Dialog open onOpenChange={open => !open && onClose()}>
        <DialogContent
          data-pdf-preview-mode-open={pdfPreviewModeOpen ? 'true' : undefined}
          className={`h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-hidden flex flex-col gap-0 p-0 ${
            pdfPreviewModeOpen
              ? '[&>button.absolute.right-4.top-4]:hidden [&>button]:hidden'
              : ''
          }`}
          onEscapeKeyDown={event => event.preventDefault()}
        >
          <DialogHeader className='border-b px-6 py-4'>
            <DialogTitle>
              Claim Worksheet{effectiveReadOnly ? ' (read-only)' : ''}
            </DialogTitle>
            <DialogDescription className='sr-only'>
              Patient and billing-cycle details with editable UB-04 worksheet
              fields.
            </DialogDescription>
            <p className='text-sm text-muted-foreground'>
              {chartPath ? (
                <Link
                  to={chartPath}
                  className='font-medium text-primary underline-offset-4 hover:underline'
                >
                  {patientName}
                </Link>
              ) : (
                patientName
              )}
              {` · ${headerRest}`}
              {chartPath ? (
                <>
                  {' · '}
                  <Link
                    to={chartPath}
                    className='font-medium text-primary underline-offset-4 hover:underline'
                  >
                    View episode <span aria-hidden='true'>↗</span>
                  </Link>
                </>
              ) : null}
            </p>
          </DialogHeader>

          {isLoading ? (
            <div className='flex flex-1 flex-col items-center justify-center py-16 text-center'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              <p className='mt-2 text-sm text-muted-foreground'>
                Loading claim worksheet...
              </p>
            </div>
          ) : error || !worksheet ? (
            <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
              <Alert
                className={
                  worksheetMissing
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-red-200 bg-red-50'
                }
              >
                {worksheetMissing ? (
                  <AlertTriangle className='h-4 w-4 text-amber-600' />
                ) : (
                  <AlertCircle className='h-4 w-4 text-red-600' />
                )}
                <AlertDescription
                  className={
                    worksheetMissing ? 'text-amber-800' : 'text-red-800'
                  }
                >
                  {error ?? 'Worksheet unavailable.'}
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <main
              data-testid='claim-worksheet-scroll-region'
              className='min-h-0 flex-1 overflow-y-auto bg-slate-50/70 px-6 py-4'
            >
              <div className='grid gap-6 xl:grid-cols-[minmax(0,1fr)_32rem]'>
                <div className='space-y-6'>
                  {!historicalWorksheet ? (
                    <>
                      {renderFieldSection('Patient', patientFields)}
                      {renderFieldSection('Physician', physicianFields)}
                      {renderFieldSection(
                        'Episode & Admission',
                        episodeAdmissionFields
                      )}
                    </>
                  ) : null}

                  {worksheetReason(worksheet) ? (
                    <Alert className='flex items-start gap-2 border-amber-200 bg-amber-50 [&>svg+div]:translate-y-0 [&>svg]:static [&>svg]:left-auto [&>svg]:top-auto [&>svg~*]:pl-0'>
                      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
                      <AlertDescription className='min-w-0 text-amber-800'>
                        {worksheetReason(worksheet)}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <div className='space-y-1 border-t pt-5'>
                    <h2 className='text-sm font-semibold text-foreground'>
                      Claim worksheet fields
                    </h2>
                    <p className='text-xs text-muted-foreground'>
                      {historicalWorksheet
                        ? 'Read-only — this abandoned claim version is preserved.'
                        : readOnly
                          ? 'Read-only — this claim has been approved.'
                          : 'Editable while the claim is pending approval. Diagnoses are snapshotted from the episode worksheet.'}
                    </p>
                  </div>

                  {!historicalWorksheet && payerReasonsError ? (
                    <Alert
                      className='border-amber-200 bg-amber-50'
                      data-testid='worksheet-payer-reason-error'
                    >
                      <AlertCircle className='h-4 w-4 text-amber-600' />
                      <AlertDescription className='text-amber-800'>
                        {payerReasonsError}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {showPayerReasons ? (
                    <Alert
                      className='border-red-200 bg-red-50'
                      data-testid='worksheet-payer-reason'
                    >
                      <AlertCircle className='h-4 w-4 text-red-600' />
                      <AlertDescription className='min-w-0 text-red-800'>
                        <p className='mb-2 text-sm font-medium'>
                          What the payer said
                        </p>
                        <PayerReasonList
                          reasons={payerReasons}
                          payerClaimNumber={payerClaimNumber}
                        />
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {validationErrorItems.length > 0 ||
                  validationWarningItems.length > 0 ? (
                    <div ref={validationSummaryRef} className='space-y-2'>
                      {validationErrorItems.length > 0 ? (
                        <Alert
                          className='border-red-200 bg-red-50'
                          data-testid='worksheet-validation-summary'
                        >
                          <AlertCircle className='h-4 w-4 text-red-600' />
                          <AlertDescription className='min-w-0 text-red-800'>
                            <IntakeValidationErrorList
                              title={
                                historicalWorksheet
                                  ? `This preserved version has ${validationErrorItems.length} ${
                                      validationErrorItems.length === 1
                                        ? 'field finding'
                                        : 'field findings'
                                    }`
                                  : `Fix ${validationErrorItems.length} ${
                                      validationErrorItems.length === 1
                                        ? 'field'
                                        : 'fields'
                                    } before sending this claim`
                              }
                              ariaLabel='Claim worksheet validation errors'
                              items={validationErrorItems}
                            />
                          </AlertDescription>
                        </Alert>
                      ) : null}
                      {validationWarningItems.length > 0 ? (
                        <Alert
                          className='border-amber-200 bg-amber-50'
                          data-testid='worksheet-validation-warnings'
                        >
                          <AlertTriangle className='h-4 w-4 text-amber-600' />
                          <AlertDescription className='min-w-0 text-amber-800'>
                            <IntakeValidationErrorList
                              title={
                                historicalWorksheet
                                  ? `This preserved version has ${validationWarningItems.length} ${
                                      validationWarningItems.length === 1
                                        ? 'warning'
                                        : 'warnings'
                                    }`
                                  : `${validationWarningItems.length} ${
                                      validationWarningItems.length === 1
                                        ? 'warning'
                                        : 'warnings'
                                    } — this claim can still be sent`
                              }
                              ariaLabel='Claim worksheet validation warnings'
                              items={validationWarningItems}
                            />
                          </AlertDescription>
                        </Alert>
                      ) : null}
                    </div>
                  ) : null}

                  {fullEdit && (historicalWorksheet || !requiresRefresh) ? (
                    <fieldset
                      disabled={effectiveReadOnly || requiresRefresh}
                      className='space-y-6 min-w-0 border-0 p-0 m-0'
                    >
                      {!historicalWorksheet ? (
                        <div className='flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
                          <p className='text-sm text-muted-foreground'>
                            Common claim fields are shown first.
                          </p>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() =>
                              setShowAllFields(current => !current)
                            }
                          >
                            {showAllFields
                              ? 'Show fewer fields'
                              : 'Show all fields'}
                          </Button>
                        </div>
                      ) : null}
                      {showAllFields ? (
                        <ShowAllUb04Fields
                          value={fullEdit.ub04}
                          disabled={effectiveReadOnly}
                          errors={errorMap}
                          onChange={ub04 => setFullEdit({ ub04 })}
                        />
                      ) : null}

                      <EditSection title='Claim identifiers & dates'>
                        <div className='grid gap-3 md:grid-cols-4'>
                          <label className='space-y-1 text-xs text-muted-foreground'>
                            Patient control number
                            <Input
                              inputSize='sm'
                              value={fullEdit.ub04.patient_control_number}
                              disabled
                            />
                          </label>
                          <label className='space-y-1 text-xs text-muted-foreground'>
                            FL 3b Medical record number
                            <Input
                              inputSize='sm'
                              value={fullEdit.ub04.medical_record_number ?? ''}
                              onChange={event =>
                                updateFullUb04({
                                  medical_record_number: event.target.value,
                                })
                              }
                            />
                          </label>
                          <label
                            className='space-y-1 text-xs text-muted-foreground'
                            data-field-anchor={errorKey('04')}
                          >
                            FL 4 Type of bill
                            <Input
                              inputSize='sm'
                              value={fullEdit.ub04.type_of_bill ?? ''}
                              aria-invalid={
                                fieldError(errorMap, '04') ? true : undefined
                              }
                              aria-describedby={
                                fieldError(errorMap, '04')
                                  ? 'ub04-04-error'
                                  : undefined
                              }
                              className={
                                fieldError(errorMap, '04')
                                  ? worksheetErrorInputClass
                                  : undefined
                              }
                              onChange={event =>
                                updateFullUb04({
                                  type_of_bill: event.target.value,
                                })
                              }
                            />
                            <WorksheetFieldError
                              id='ub04-04-error'
                              error={fieldError(errorMap, '04')}
                            />
                          </label>
                          <WorksheetDateInput
                            label='FL 6 Statement from'
                            value={fullEdit.ub04.statement_from}
                            error={fieldError(errorMap, '06-from')}
                            fieldAnchor={errorKey('06-from')}
                            onChange={statement_from =>
                              updateFullUb04({ statement_from })
                            }
                          />
                          <WorksheetDateInput
                            label='FL 6 Statement through'
                            value={fullEdit.ub04.statement_through}
                            error={fieldError(errorMap, '06-through')}
                            fieldAnchor={errorKey('06-through')}
                            onChange={statement_through =>
                              updateFullUb04({ statement_through })
                            }
                          />
                          <WorksheetDateInput
                            label='FL 12 Admission date'
                            value={fullEdit.ub04.admission_date}
                            onChange={admission_date =>
                              updateFullUb04({ admission_date })
                            }
                          />
                          <label
                            className='space-y-1 text-xs text-muted-foreground'
                            data-field-anchor={errorKey('14')}
                          >
                            FL 14 Admission type
                            <UB04CodeSelect
                              ariaLabel='FL 14 Admission type'
                              options={UB04_ADMISSION_TYPE_OPTIONS}
                              value={fullEdit.ub04.admission_type}
                              error={fieldError(errorMap, '14')}
                              onValueChange={admission_type =>
                                updateFullUb04({ admission_type })
                              }
                            />
                          </label>
                          <label
                            className='space-y-1 text-xs text-muted-foreground'
                            data-field-anchor={errorKey('15')}
                          >
                            FL 15 Admission source
                            <UB04CodeSelect
                              ariaLabel='FL 15 Admission source'
                              options={UB04_ADMISSION_SOURCE_OPTIONS}
                              value={fullEdit.ub04.admission_source}
                              error={fieldError(errorMap, '15')}
                              onValueChange={admission_source =>
                                updateFullUb04({ admission_source })
                              }
                            />
                          </label>
                          <label
                            className='space-y-1 text-xs text-muted-foreground'
                            data-field-anchor={errorKey('17')}
                          >
                            FL 17 Patient status
                            <UB04CodeSelect
                              ariaLabel='FL 17 Patient status'
                              options={UB04_PATIENT_STATUS_OPTIONS}
                              value={fullEdit.ub04.patient_status}
                              error={fieldError(errorMap, '17')}
                              onValueChange={handlePatientStatusChange}
                            />
                          </label>
                        </div>
                      </EditSection>

                      {/* Side by side when the column is wide enough; each
                          section keeps its current width. */}
                      <div className='grid gap-6 md:grid-cols-2 min-[1700px]:grid-cols-3'>
                        <EditSection title='FL 18-28 Condition codes'>
                          <ConditionCodesField
                            values={fullEdit.ub04.condition_codes ?? []}
                            onChange={condition_codes =>
                              setFullEdit({
                                ub04: { ...fullEdit.ub04, condition_codes },
                              })
                            }
                          />
                        </EditSection>

                        <EditSection title='FL 31-36 Occurrence codes'>
                          <OccurrenceCodesField
                            rows={(fullEdit.ub04.occurrence_uses ?? [])
                              .filter(use => use.kind === 'occurrence')
                              .map(use => ({
                                code: use.code,
                                date: use.date ?? '',
                              }))}
                            maxRows={Math.min(
                              MAX_WORKSHEET_OCCURRENCE_CODES,
                              12 -
                                (fullEdit.ub04.occurrence_uses ?? []).filter(
                                  use => use.kind === 'span'
                                ).length
                            )}
                            onChange={rows => {
                              const spans = (
                                fullEdit.ub04.occurrence_uses ?? []
                              ).filter(use => use.kind === 'span');
                              const spanPositions = new Set(
                                spans.map(span => span.position)
                              );
                              const occurrencePositions = (
                                [
                                  '31a',
                                  '32a',
                                  '33a',
                                  '34a',
                                  '31b',
                                  '32b',
                                  '33b',
                                  '34b',
                                  '35a',
                                  '36a',
                                  '35b',
                                  '36b',
                                ] as const
                              ).filter(
                                position => !spanPositions.has(position)
                              );
                              setFullEdit({
                                ub04: {
                                  ...fullEdit.ub04,
                                  occurrence_uses: [
                                    ...rows.map((row, index) => ({
                                      position:
                                        occurrencePositions[index] ?? '31a',
                                      kind: 'occurrence' as const,
                                      code: row.code,
                                      date: row.date || null,
                                    })),
                                    ...spans,
                                  ],
                                },
                              });
                            }}
                          />
                        </EditSection>

                        <EditSection title='FL 39-41 Value codes'>
                          <ValueCodesField
                            rows={fullEdit.ub04.value_codes ?? []}
                            onChange={value_codes =>
                              setFullEdit({
                                ub04: { ...fullEdit.ub04, value_codes },
                              })
                            }
                          />
                        </EditSection>
                      </div>

                      <ServiceLinesSection
                        value={fullEdit.ub04.service_lines ?? []}
                        disabled={effectiveReadOnly}
                        onChange={handleServiceLinesChange}
                      />

                      <EditSection title='FL 54, 55 & 64 Claim payment and control'>
                        <div className='grid gap-3 md:grid-cols-3'>
                          <label className='space-y-1 text-xs text-muted-foreground'>
                            FL 54 Prior payment
                            <CurrencyInput
                              inputSize='sm'
                              value={getPrimaryPayer().prior_payment ?? ''}
                              onValueChange={prior_payment =>
                                updatePrimaryPayerField(
                                  'prior_payment',
                                  prior_payment
                                )
                              }
                            />
                          </label>
                          <label className='space-y-1 text-xs text-muted-foreground'>
                            FL 55 Estimated amount due
                            <CurrencyInput
                              inputSize='sm'
                              value={
                                getPrimaryPayer().estimated_amount_due ?? ''
                              }
                              onValueChange={estimated_amount_due =>
                                updatePrimaryPayerField(
                                  'estimated_amount_due',
                                  estimated_amount_due
                                )
                              }
                            />
                          </label>
                          <label
                            className='space-y-1 text-xs text-muted-foreground'
                            data-field-anchor={errorKey('64', 'A')}
                          >
                            FL 64 Document control number
                            <Input
                              inputSize='sm'
                              value={
                                getPrimaryPayer().document_control_number ?? ''
                              }
                              aria-invalid={
                                primaryPayerError('64') ? true : undefined
                              }
                              aria-describedby={
                                primaryPayerError('64')
                                  ? 'ub04-64-error'
                                  : undefined
                              }
                              className={
                                primaryPayerError('64')
                                  ? worksheetErrorInputClass
                                  : undefined
                              }
                              onChange={event =>
                                updatePrimaryPayerField(
                                  'document_control_number',
                                  event.target.value
                                )
                              }
                            />
                            <WorksheetFieldError
                              id='ub04-64-error'
                              error={primaryPayerError('64')}
                            />
                          </label>
                        </div>
                      </EditSection>

                      <PayerInsuredSection
                        value={fullEdit.ub04.payer_rows ?? []}
                        disabled={effectiveReadOnly}
                        errors={errorMap}
                        onChange={payer_rows =>
                          setFullEdit({
                            ub04: { ...fullEdit.ub04, payer_rows },
                          })
                        }
                      />

                      <EditSection title='FL 76-79 Providers'>
                        <div className='space-y-4'>
                          {[
                            {
                              slot: 'attending' as const,
                              locator: '76',
                              title: 'Attending',
                            },
                            {
                              slot: 'operating' as const,
                              locator: '77',
                              title: 'Operating',
                            },
                            {
                              slot: 'other1' as const,
                              locator: '78',
                              title: 'Other provider 1',
                            },
                            {
                              slot: 'other2' as const,
                              locator: '79',
                              title: 'Other provider 2',
                            },
                          ].map(providerConfig => {
                            const provider = getProvider(providerConfig.slot);
                            return (
                              <div
                                key={providerConfig.slot}
                                className='rounded-lg border border-slate-200 bg-slate-50/70 p-3'
                              >
                                <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                  FL {providerConfig.locator}{' '}
                                  {providerConfig.title}
                                </div>
                                <div className='grid gap-3 md:grid-cols-3'>
                                  <label className='space-y-1 text-xs text-muted-foreground'>
                                    FL {providerConfig.locator}{' '}
                                    {providerConfig.title} NPI
                                    <Input
                                      inputSize='sm'
                                      value={provider.npi ?? ''}
                                      onChange={event =>
                                        updateProviderField(
                                          providerConfig.slot,
                                          'npi',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className='space-y-1 text-xs text-muted-foreground'>
                                    FL {providerConfig.locator}{' '}
                                    {providerConfig.title} last name
                                    <Input
                                      inputSize='sm'
                                      value={provider.last_name ?? ''}
                                      onChange={event =>
                                        updateProviderField(
                                          providerConfig.slot,
                                          'last_name',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className='space-y-1 text-xs text-muted-foreground'>
                                    FL {providerConfig.locator}{' '}
                                    {providerConfig.title} first name
                                    <Input
                                      inputSize='sm'
                                      value={provider.first_name ?? ''}
                                      onChange={event =>
                                        updateProviderField(
                                          providerConfig.slot,
                                          'first_name',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className='space-y-1 text-xs text-muted-foreground'>
                                    FL {providerConfig.locator}{' '}
                                    {providerConfig.title} qualifier
                                    <Input
                                      inputSize='sm'
                                      value={provider.qualifier ?? ''}
                                      onChange={event =>
                                        updateProviderField(
                                          providerConfig.slot,
                                          'qualifier',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className='space-y-1 text-xs text-muted-foreground'>
                                    FL {providerConfig.locator}{' '}
                                    {providerConfig.title} other ID
                                    <Input
                                      inputSize='sm'
                                      value={provider.other_id ?? ''}
                                      onChange={event =>
                                        updateProviderField(
                                          providerConfig.slot,
                                          'other_id',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </EditSection>

                      <EditSection title='FL 80 Remarks'>
                        <label className='space-y-1 text-xs text-muted-foreground'>
                          FL 80 Remarks
                          <Textarea
                            textareaSize='sm'
                            value={fullEdit.ub04.remarks ?? ''}
                            aria-invalid={remarksTooLong}
                            aria-describedby='ub04-remarks-limit'
                            onChange={event =>
                              updateFullUb04({ remarks: event.target.value })
                            }
                          />
                        </label>
                        <span
                          id='ub04-remarks-limit'
                          className={
                            remarksTooLong
                              ? 'text-red-600'
                              : 'text-muted-foreground'
                          }
                        >
                          {remarksLength}/{MAX_UB04_REMARKS_LENGTH} characters
                          {remarksTooLong
                            ? ` — FL 80 remarks supports ${MAX_UB04_REMARKS_LENGTH} characters.`
                            : ''}
                        </span>
                      </EditSection>
                    </fieldset>
                  ) : historicalWorksheet ? null : (
                    <Alert className='border-amber-200 bg-amber-50'>
                      <AlertTriangle className='h-4 w-4 text-amber-600' />
                      <AlertDescription className='text-amber-800'>
                        Refresh worksheet source data before editing.
                      </AlertDescription>
                    </Alert>
                  )}

                  {!historicalWorksheet ? (
                    visitsError ? (
                      <Alert className='border-red-200 bg-red-50'>
                        <AlertCircle className='h-4 w-4 text-red-600' />
                        <AlertDescription className='text-red-800'>
                          {visitsError}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70'>
                        <VisitSupplyBillableSection
                          visitsData={visitsData}
                          orders={preview?.orders ?? []}
                          patientId={billingCycle.patient_id}
                          episodeId={billingCycle.episode_id}
                          readOnly={
                            effectiveReadOnly ||
                            billingCycle.billing_cycle_status !==
                              'PENDING_APPROVAL'
                          }
                          canViewOrders={canViewOrders}
                          onVisitLinkClick={navigateAndClose}
                          pendingBillingStatusChanges={
                            pendingBillingStatusChanges
                          }
                          onBillableStatusChange={(visitId, status) =>
                            setPendingBillingStatusChanges(prev => ({
                              ...prev,
                              [visitId]: status,
                            }))
                          }
                          pendingBulkSupplyCost={pendingBulkSupplyCost}
                          onBulkSupplyCostChange={setPendingBulkSupplyCost}
                          onSaveSourceData={() => void saveSourceData()}
                          isSavingSourceData={isSavingSourceData}
                        />
                      </div>
                    )
                  ) : null}
                </div>

                {!historicalWorksheet ? (
                  <div className='xl:sticky xl:top-0 xl:self-start'>
                    <SavedUb04Preview
                      billingCycleId={billingCycleId}
                      reloadKey={previewReloadKey}
                      stale={dirty}
                      disabled={requiresRefresh}
                      onPreviewModeChange={setPdfPreviewModeOpen}
                    />
                  </div>
                ) : null}
              </div>
            </main>
          )}

          <div className='flex items-center justify-end gap-2 border-t px-6 py-4'>
            {saveError && (
              <span className='mr-auto text-sm text-red-600'>{saveError}</span>
            )}
            {effectiveReadOnly ? (
              <Button variant='outline' onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant='outline'
                  onClick={() => setRefreshConfirmOpen(true)}
                  disabled={
                    busy || isLoading || (!worksheet && !worksheetMissing)
                  }
                >
                  {isRefreshing ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='mr-2 h-4 w-4' />
                  )}
                  Refresh worksheet data
                </Button>
                <Button variant='outline' onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant='outline'
                  onClick={() => requestSave('save')}
                  disabled={busy || isLoading || !canSaveFull}
                >
                  <Save className='mr-2 h-4 w-4' />
                  Save
                </Button>
                {/* EMR-1409: a replacement cycle sits in CORRECTION_DRAFT
                    until its draft is approved, and the backend accepts that
                    status as a source for READY_TO_SEND — so the biller gets
                    the same Save & Approve action as PENDING_APPROVAL. */}
                {billingCycle.billing_cycle_status === 'PENDING_APPROVAL' ||
                billingCycle.billing_cycle_status === 'CORRECTION_DRAFT' ? (
                  <Button
                    variant='purple'
                    onClick={() => setApproveConfirmOpen(true)}
                    disabled={busy || isLoading || !canSaveFull}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      'Save & Approve'
                    )}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={refreshConfirmOpen}
        title='Refresh worksheet data?'
        message='This replaces every worksheet field with current patient, episode, insurance, visit, provider, and billing data. All manual worksheet edits will be overwritten.'
        confirmLabel='Refresh & overwrite'
        onAccept={() => {
          setRefreshConfirmOpen(false);
          void handleRefresh();
        }}
        onCancel={() => setRefreshConfirmOpen(false)}
      />
      <ConfirmationDialog
        open={approveConfirmOpen}
        title='Approve billing cycle?'
        message='This action cannot be undone, please make sure all information is correct'
        confirmLabel='Approve'
        onAccept={() => {
          setApproveConfirmOpen(false);
          requestSave('saveAndApprove');
        }}
        onCancel={() => setApproveConfirmOpen(false)}
      />
      <Dialog
        open={chargesConfirm !== null}
        onOpenChange={open => !open && setChargesConfirm(null)}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Update estimated amount due?</DialogTitle>
            <DialogDescription>
              {chargesConfirm
                ? `Service line charges total ${chargesConfirm.total}. FL 55 Estimated amount due is ${chargesConfirm.currentEstimate}.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='flex-col gap-2 sm:flex-row sm:justify-end sm:gap-0'>
            <Button variant='outline' onClick={() => setChargesConfirm(null)}>
              Cancel
            </Button>
            <Button variant='outline' onClick={confirmSaveKeepingEstimate}>
              Save and keep current value
            </Button>
            <Button variant='purple' onClick={confirmSaveWithTotal}>
              Save and overwrite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={tobChangeProposal !== null}
        title='Update type of bill?'
        message={`FL 17 now indicates a discharge. Change FL 4 Type of bill from ${
          tobChangeProposal?.from ?? ''
        } to ${tobChangeProposal?.to ?? ''}?`}
        confirmLabel='Update FL 4'
        onAccept={() => {
          if (tobChangeProposal) {
            updateFullUb04({ type_of_bill: tobChangeProposal.to });
          }
          setTobChangeProposal(null);
        }}
        onCancel={() => setTobChangeProposal(null)}
      />
    </>
  );
}
